// StatusGroupsProvider — loads the per-workflow-state display groups once for the signed-in console
// (task 355.10 api) and shares them through StatusGroupsContext. Failure is quiet: pills fall back to
// the client map, which is never wrong in a dangerous way (unknown → neutral).

import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getWorkflowStatusGroups } from '../../api/endpoints/workflows';
import { queryKeys } from '../../api/queryKeys';
import {
  StatusGroupsContext,
  EMPTY_STATUS_GROUPS,
  type StatusGroupMap,
} from './statusGroupsContext';
import { statusGroupKey, type StatusGroup } from './statusGroups';

export default function StatusGroupsProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const { data } = useQuery({
    queryKey: queryKeys.workflows.statusGroups(),
    queryFn: getWorkflowStatusGroups,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const map = useMemo<StatusGroupMap>(() => {
    if (!data?.length) return EMPTY_STATUS_GROUPS;
    const m = new Map<string, StatusGroup>();
    for (const entry of data) {
      if (entry.group)
        m.set(statusGroupKey(entry.workflowCode, entry.stateCode), entry.group as StatusGroup);
    }
    return m;
  }, [data]);

  return <StatusGroupsContext.Provider value={map}>{children}</StatusGroupsContext.Provider>;
}
